import { createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery } from './baseQuery';
import {
  CollectionConstraintType,
  CompanyInfo,
  ContractDetail,
  ContractDocumentType,
  ContractFormData,
  GetContractCondition,
  GetContractResponse,
  WasteItemsInfo,
} from '../types/contracts';
import { extinctNullFromParams } from './nullExtict';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';
import { CarRelatedWithContract } from '../types/car';
import { DriverRelatedWithContract } from '../types/driver';

type postContractRequest = {
  receivedAt: string;
  salesPicId: number;
  contractDocumentTypeId: number;
  supplierCompanyInfo: postContractRequestCompanyInfo;
  contractorCompanyInfo?: postContractRequestCompanyInfo;
  wastePointCompanyInfo?: postContractRequestCompanyInfo;
  carCategoryIds: number[];
  collectionConstraintTypeId: number;
  collectionConstraintWeekDays?: number[];
  collectionConstraintDateStart: string;
  collectionConstraintDateEnd?: string;
  collectionConstraintTimeStart?: string;
  collectionConstraintTimeEnd?: string;
  collectionEstimatedTime: number;
  goesStraight: boolean;
  requiredSkillLevelId: number | null;
  requiredQualificationIds: number[];
  memo?: string;
  wasteItemsInfo: WasteItemsInfo[];
};
type postContractRequestCompanyInfo = {
  companyId: number;
  companyPicName?: string;
  phoneNumber?: string;
};

type getCarsRelatedWithContractRequest = {
  contractId: number;
  userId: number;
  date: string;
};
type getCarsRelatedWithContractResponse = {
  cars: CarRelatedWithContract[];
  total: number;
};
type getDriversRelatedWithContractRequest = {
  contractId: number;
  carId: number;
  date: string;
};
type getDriversRelatedWithContractResponse = {
  drivers: DriverRelatedWithContract[];
  total: number;
};

/**
 * GetContractConditions から クエリパラメータ用オブジェクトを作成する
 * null や undefined な項目はキーごと除外する
 * @param conditions
 * @returns
 */
const convertGetContractConditionsToRequest = (
  conditions: GetContractCondition
): GetContractCondition =>
  extinctNullFromParams<GetContractCondition>({
    // contractNo は空文字列も消去
    contractNo: conditions.contractNo || undefined,
    // startDate は空文字列も消去
    startDate: conditions.startDate || undefined,
    // endDate は空文字列も消去
    endDate: conditions.endDate || undefined,
    // companyName は空文字列も消去
    companyName: conditions.companyName || undefined,
    calcStatus: conditions.calcStatus,
    goesStraight: conditions.goesStraight,
    // wasteItemIds は空配列も消去
    wasteItemIds: conditions.wasteItemIds?.length
      ? conditions.wasteItemIds
      : undefined,
    limit: conditions.limit,
    offset: conditions.offset,
    page: conditions.page,
  });

export const contractApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'contract',
  tagTypes: ['Contract', ...errorTagTypes],
  endpoints: (build) => ({
    getContracts: build.query<GetContractResponse, GetContractCondition>({
      query: (condition) => ({
        url: `/contracts`,
        params: convertGetContractConditionsToRequest(condition),
      }),
      providesTags: (res, error) => [
        ...(res?.contracts || []).map((c) => ({
          type: 'Contract' as const,
          id: c.contractId,
        })),
        { type: 'Contract', id: 'List' },
        ...handleErrorTags(error),
      ],
    }),
    createContract: build.mutation({
      query: (body: postContractRequest) => ({
        url: `/contracts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {
          type: 'Contract',
          id: 'List',
        },
      ],
    }),
    getContractDetail: build.query<ContractDetail, number>({
      query: (id: number) => ({
        url: `/contracts/${id}`,
      }),
      providesTags: (_res, error, id) => [
        { type: 'Contract', id },
        ...handleErrorTags(error),
      ],
    }),
    editContract: build.mutation({
      query: ({
        id,
        contract,
      }: {
        id: number;
        contract: postContractRequest;
      }) => ({
        url: `/contracts/${id}`,
        method: 'PUT',
        body: contract,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        {
          type: 'Contract',
          id,
        },
        { type: 'Contract', id: 'List' },
      ],
    }),
    deleteContract: build.mutation({
      query: (id: number) => ({
        url: `/contracts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'List' },
      ],
    }),
    getContractDocumentTypes: build.query<ContractDocumentType[], void>({
      query: () => ({
        url: `/contract/document-types`,
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    getCollectionConstraintTypes: build.query<CollectionConstraintType[], void>(
      {
        query: () => ({
          url: `/contract/collection-constraint-types`,
        }),
        providesTags: (_res, error) => handleErrorTags(error),
      }
    ),
    getCarsRelatedWithContract: build.query<
      getCarsRelatedWithContractResponse,
      getCarsRelatedWithContractRequest
    >({
      query: ({ contractId, ...params }) => ({
        url: `/contract/${contractId}/available-cars`,
        params,
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    getDriversRelatedWithContract: build.query<
      getDriversRelatedWithContractResponse,
      getDriversRelatedWithContractRequest
    >({
      query: ({ contractId, ...params }) => ({
        url: `/contract/${contractId}/available-drivers`,
        params,
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
  }),
});

export const {
  useGetContractsQuery,
  useGetContractDetailQuery,
  useCreateContractMutation,
  useEditContractMutation,
  useDeleteContractMutation,
  useGetContractDocumentTypesQuery,
  useGetCollectionConstraintTypesQuery,
  useGetCarsRelatedWithContractQuery,
  useGetDriversRelatedWithContractQuery,
} = contractApi;

export function createPostContractRequestFromFormData(
  formData: ContractFormData
): postContractRequest {
  /* eslint-disable @typescript-eslint/no-non-null-assertion -- React Hook Form 側での required などでチェック済みの想定 */
  return {
    receivedAt: formData.receivedAt,
    salesPicId: formData.salesPic!.id,
    contractDocumentTypeId: formData.contractDocumentTypeId!,
    supplierCompanyInfo: createCompanyInfo(formData.supplierCompanyInfo),
    contractorCompanyInfo: formData.contractorCompanyInfo
      ? createCompanyInfo(formData.contractorCompanyInfo)
      : undefined,
    wastePointCompanyInfo:
      formData.wastePointCompanyInfo &&
      !formData.wastePointCompanyIsTheSameSupplier
        ? createCompanyInfo(formData.wastePointCompanyInfo)
        : undefined,
    carCategoryIds: formData.carCategoryIds,
    collectionConstraintTypeId: formData.collectionConstraintTypeId!,
    collectionConstraintWeekDays:
      formData.collectionConstraintTypeId === /* 「定期」 */ 1
        ? formData.collectionConstraintWeekDays
        : undefined,
    collectionConstraintDateStart: formData.collectionConstraintDateStart,
    collectionConstraintDateEnd:
      (formData.collectionConstraintTypeId !== /* 「スポット」 */ 2 &&
        formData.collectionConstraintDateEnd) ||
      undefined,
    collectionConstraintTimeStart:
      formData.collectionConstraintTimeStart || undefined,
    collectionConstraintTimeEnd:
      formData.collectionConstraintTimeEnd || undefined,
    collectionEstimatedTime: formData.collectionEstimatedTime!,
    goesStraight: formData.goesStraight,
    requiredSkillLevelId:
      formData.requiredSkillLevelId === 0
        ? null
        : formData.requiredSkillLevelId!,
    requiredQualificationIds: formData.requiredQualificationIds,
    memo: formData.memo || undefined,
    wasteItemsInfo: formData.wasteItemsInfo,
  };

  function createCompanyInfo(
    formData: CompanyInfo
  ): postContractRequestCompanyInfo {
    return {
      companyId: formData.companyId!,
      companyPicName: formData.companyPicName || undefined,
      phoneNumber: formData.phoneNumber || undefined,
    };
  }
}
